import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const DEFAULT_GET_OPTION_VALUE = (option) => {
  if (typeof option === "string") return option;
  return option?.value;
};

const DEFAULT_GET_OPTION_LABEL = (option) => {
  if (typeof option === "string") return option;
  return option?.label ?? option?.value ?? "";
};

const FilterDropdown = ({
  // Content
  options,
  selectedValues,
  onToggle,
  onClear,

  // Labels
  label,
  buttonText = "Filter",
  title,
  dropdownTitle = "Filter",
  searchPlaceholder = "Search...",
  emptyText = "No options found",

  // Behavior
  enableSearch = true,
  isOpen: controlledIsOpen,
  onOpenChange,

  // Option mapping/render
  getOptionValue = DEFAULT_GET_OPTION_VALUE,
  getOptionLabel = DEFAULT_GET_OPTION_LABEL,
  renderOptionSuffix,

  // Styling
  activeBackgroundColor = "#1976d2",
  activeTextColor = "white",
  inactiveTextColor = "#333",
  buttonBorderColor = "#ddd",
  buttonFontSize = "0.8em",
  minWidth = "250px",
  maxHeight,

  // DOM hooks
  buttonId,
}) => {
  const rootRef = useRef(null);
  const dropdownRef = useRef(null);
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [computedMaxHeight, setComputedMaxHeight] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const parsePx = (value) => {
    if (typeof value === "number") return value;
    if (typeof value !== "string") return null;
    const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/);
    if (!match) return null;
    return Number(match[1]);
  };

  const clamp = (value, min, max) => {
    return Math.min(Math.max(value, min), max);
  };

  const isControlled = typeof controlledIsOpen === "boolean";
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;

  const selectedCount = Array.isArray(selectedValues) ? selectedValues.length : 0;
  const selectedLabels = useMemo(() => {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) return [];
    const values = new Set(selectedValues.map((value) => String(value)));
    return (options || [])
      .map((option) => {
        const value = String(getOptionValue(option));
        if (!values.has(value)) return null;
        return String(getOptionLabel(option) || value);
      })
      .filter(Boolean);
  }, [selectedValues, options, getOptionValue, getOptionLabel]);

  const selectedLabelsText = useMemo(() => {
    if (selectedLabels.length === 0) return "";
    const joined = selectedLabels.join(", ");
    if (joined.length <= 60) return joined;
    return `${joined.slice(0, 57)}...`;
  }, [selectedLabels]);

  const setIsOpen = useCallback((next) => {
    if (!isControlled) {
      setUncontrolledIsOpen(next);
    }
    if (onOpenChange) {
      onOpenChange(next);
    }
  }, [isControlled, onOpenChange]);

  const filteredOptions = useMemo(() => {
    if (!enableSearch) return options;
    const query = (searchQuery || "").trim().toLowerCase();
    if (!query) return options;
    return (options || []).filter((option) => {
      const rawLabel = getOptionLabel(option);
      const labelText = String(rawLabel || "").toLowerCase();
      return labelText.includes(query);
    });
  }, [options, enableSearch, searchQuery, getOptionLabel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      const root = rootRef.current;
      const dropdown = dropdownRef.current;
      if (root && root.contains(event.target)) return;
      if (dropdown && dropdown.contains(event.target)) return;
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  // Make dropdown as tall as viewport allows (flip up if needed)
  useEffect(() => {
    if (!isOpen) return;
    if (maxHeight) return; // explicit maxHeight overrides auto sizing

    const compute = () => {
      const root = rootRef.current;
      if (!root) return;

      const rect = root.getBoundingClientRect();
      const gutter = 12; // keep away from viewport edges
      const below = Math.max(0, window.innerHeight - rect.bottom - gutter);
      const above = Math.max(0, rect.top - gutter);

      const shouldOpenTop = below < 240 && above > below;
      const available = shouldOpenTop ? above : below;

      setComputedMaxHeight(`${Math.max(160, Math.floor(available))}px`);

      const estimatedWidth = parsePx(minWidth) || rect.width || 250;
      const left = clamp(rect.left, gutter, Math.max(gutter, window.innerWidth - gutter - estimatedWidth));
      const top = shouldOpenTop ? null : rect.bottom + 4;
      const bottom = shouldOpenTop ? window.innerHeight - rect.top + 4 : null;
      setDropdownPosition({ left, top, bottom });
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [isOpen, maxHeight, minWidth]);

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      {label ? (
        <span style={{ marginRight: "5px" }}>
          {label}
          {selectedLabelsText ? (
            <span style={{ marginLeft: "6px", color: "#666" }} title={selectedLabels.join(", ")}>
              {selectedLabelsText}
            </span>
          ) : null}
        </span>
      ) : null}
      <span
        id={buttonId}
        data-filter-button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        style={{
          padding: "4px 8px",
          borderRadius: "4px",
          backgroundColor: selectedCount > 0 ? activeBackgroundColor : "transparent",
          color: selectedCount > 0 ? activeTextColor : inactiveTextColor,
          fontSize: buttonFontSize,
          fontWeight: "normal",
          cursor: "pointer",
          border: `1px solid ${buttonBorderColor}`,
          display: "inline-block",
          userSelect: "none",
        }}
        title={title}
      >
        {buttonText} {selectedCount > 0 && `(${selectedCount})`}
      </span>

      {isOpen && (
        typeof document !== "undefined"
          ? createPortal(
              <div
                ref={dropdownRef}
                data-filter-dropdown
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "fixed",
                  top: dropdownPosition?.top ?? "auto",
                  bottom: dropdownPosition?.bottom ?? "auto",
                  left: dropdownPosition?.left ?? 0,
                  zIndex: 999999,
                  backgroundColor: "white",
                  border: `1px solid ${buttonBorderColor}`,
                  borderRadius: "4px",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
                  minWidth,
                  maxHeight: maxHeight || computedMaxHeight || "70vh",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid #eee",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <span style={{ fontWeight: "600", fontSize: "0.9em" }}>{dropdownTitle}</span>
                  {selectedCount > 0 && typeof onClear === "function" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#666",
                        fontSize: "0.8em",
                        textDecoration: "underline",
                      }}
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {enableSearch && (
                  <div style={{ padding: "8px 12px", borderBottom: "1px solid #eee" }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={searchPlaceholder}
                      style={{
                        padding: "4px",
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        boxSizing: "border-box",
                        fontSize: "0.85em",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}

                <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {filteredOptions.map((option) => {
                    const value = getOptionValue(option);
                    const labelText = getOptionLabel(option);
                    const checked = Array.isArray(selectedValues)
                      ? selectedValues.includes(value)
                      : false;

                    return (
                      <label
                        key={String(value)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9f9f9")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            e.stopPropagation();
                            onToggle(value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginRight: "8px", cursor: "pointer" }}
                        />
                        <span style={{ fontSize: "0.9em" }}>
                          {labelText}
                          {typeof renderOptionSuffix === "function" ? renderOptionSuffix(option) : null}
                        </span>
                      </label>
                    );
                  })}

                  {filteredOptions.length === 0 && (
                    <div style={{ padding: "12px", color: "#999", fontSize: "0.9em" }}>
                      {emptyText}
                    </div>
                  )}
                </div>
              </div>,
              document.body
            )
          : null
      )}
    </div>
  );
};

export default FilterDropdown;
